// ─── Time frame for the Reports page ─────────────────────────────────────────
//
// Reports need TWO independent axes and the dashboard helper gives neither:
//
//   window — how far back to look, as a ROLLING range ending today ("the last
//            30 days"), not the current calendar month. `getDashboardDateRange`
//            answers the calendar question and returns a formatted display
//            string, so it cannot be fed to an endpoint at all.
//   grain  — how wide each bucket is inside that window. Only the reports with
//            a time series expose it, and it is chosen separately: "last 90
//            days, bucketed weekly" is a normal thing to ask for.
//
// Reuses `isoDay`/`parseIsoDay`/`addDays` from taskDateRange rather than
// restating day arithmetic — the Tasks worklist and Reports must never disagree
// about what a day boundary is.
//
// LOAD-BEARING, same rule as taskDateRange: the window a user sees is INCLUSIVE
// at both ends, and `toQueryWindow` is the one place it becomes the half-open
// range endpoints take. Skipping it drops the last day of every report.
//
// Pure and dependency-free so it can be unit-tested.

import { addDays, isoDay, parseIsoDay } from '@/lib/taskDateRange'
import { DASHBOARD_PERIODS, type DashboardPeriod } from '@/lib/dashboardDateRange'

/** Rolling presets, plus the escape hatch for a window no preset covers. */
export type ReportPreset = '7d' | '30d' | '90d' | 'custom'

/**
 * Bucket size for reports with a time series.
 *
 * Deliberately a SUBSET of `DashboardPeriod` — same vocabulary as the dashboard
 * so the two surfaces read alike, minus `yearly`, which cannot bucket anything
 * inside a window this page caps at 90 days by preset.
 */
export type ReportGrain = Extract<DashboardPeriod, 'daily' | 'weekly' | 'monthly'>

/** What the user sees and what a report states: both ends inclusive. */
export type ReportWindow = { from: string; to: string }

/** What an endpoint gets: `to` is EXCLUSIVE. */
export type QueryWindow = { from: string; to: string }

/** The whole filter state, and the only thing a report needs to describe itself. */
export type ReportTimeFrame = {
  preset: ReportPreset
  window: ReportWindow
  grain: ReportGrain
}

export const REPORT_PRESETS: { id: ReportPreset; label: string; days: number }[] = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
]

export const DEFAULT_PRESET: ReportPreset = '30d'
export const DEFAULT_GRAIN: ReportGrain = 'daily'

const GRAIN_IDS: ReportGrain[] = ['daily', 'weekly', 'monthly']

/** Grain options, labelled from the dashboard vocabulary so the two cannot drift. */
export const REPORT_GRAINS: { id: ReportGrain; label: string }[] = GRAIN_IDS.map((id) => ({
  id,
  label: DASHBOARD_PERIODS.find((p) => p.id === id)?.label ?? id,
}))

export function isReportGrain(value: string | null): value is ReportGrain {
  return value !== null && (GRAIN_IDS as string[]).includes(value)
}

export function isReportPreset(value: string | null): value is ReportPreset {
  return value === 'custom' || REPORT_PRESETS.some((p) => p.id === value)
}

/**
 * The inclusive window a rolling preset covers, ending TODAY.
 *
 * "Last 7 days" counts today as one of the seven — a report run on the 8th
 * showing the 1st through the 8th would be showing eight days.
 */
export function presetWindow(preset: ReportPreset, today: Date): ReportWindow {
  const days = REPORT_PRESETS.find((p) => p.id === preset)?.days ?? 30
  const to = isoDay(today)
  return { from: addDays(to, -(days - 1)), to }
}

/** Inclusive length of a window in days. A single day is 1, not 0. */
export function windowLengthDays(window: ReportWindow): number {
  const ms = parseIsoDay(window.to).getTime() - parseIsoDay(window.from).getTime()
  return Math.max(1, Math.round(ms / 86_400_000) + 1)
}

/**
 * Inclusive window → the half-open range endpoints take.
 *
 * An inverted window is normalised rather than passed through: a hand-typed
 * `to` before `from` would otherwise match nothing and read as "no data".
 */
export function toQueryWindow(window: ReportWindow): QueryWindow {
  const [from, to] = window.to < window.from ? [window.to, window.from] : [window.from, window.to]
  return { from, to: addDays(to, 1) }
}

/** e.g. "10 Aug – 8 Sep 2026", or "8 Sep 2026" for a single day. */
export function windowLabel(window: ReportWindow): string {
  const from = parseIsoDay(window.from)
  const to = parseIsoDay(window.to)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return ''

  const right = to.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  if (window.from === window.to) return right

  const sameYear = from.getFullYear() === to.getFullYear()
  const left = from.toLocaleDateString(
    undefined,
    sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' },
  )
  return `${left} – ${right}`
}

/**
 * The one line every report prints so a screenshot of it is self-describing:
 * "Last 30 days · 10 Aug – 8 Sep 2026", or "Custom range · …" when hand-picked.
 */
export function timeFrameSummary(frame: ReportTimeFrame): string {
  const preset = REPORT_PRESETS.find((p) => p.id === frame.preset)
  const scope = preset ? preset.label : `Custom range (${windowLengthDays(frame.window)} days)`
  return `${scope} · ${windowLabel(frame.window)}`
}

/**
 * Read the time frame out of the URL.
 *
 * A preset is re-derived from `today` rather than stored as dates, so a link
 * shared as "last 30 days" still means the last 30 days when it is opened
 * tomorrow. Only a custom window pins explicit dates.
 *
 * Anything unparseable falls back to the default instead of throwing — these
 * values are user-editable text in the address bar.
 */
export function parseTimeFrame(params: URLSearchParams, today: Date): ReportTimeFrame {
  const grainParam = params.get('grain')
  const grain: ReportGrain = isReportGrain(grainParam) ? grainParam : DEFAULT_GRAIN

  const from = params.get('from')
  const to = params.get('to')
  if (isIsoDay(from) && isIsoDay(to)) {
    const window = to < from ? { from: to, to: from } : { from, to }
    return { preset: 'custom', window, grain }
  }

  const rangeParam = params.get('range')
  const preset: ReportPreset =
    isReportPreset(rangeParam) && rangeParam !== 'custom' ? rangeParam : DEFAULT_PRESET
  return { preset, window: presetWindow(preset, today), grain }
}

/**
 * The time frame as URL params, to merge into a link.
 *
 * Presets travel as `range`; only a custom window writes `from`/`to`, so the
 * URL says which of the two the user actually chose.
 */
export function timeFrameParams(frame: ReportTimeFrame): Record<string, string> {
  const grain = { grain: frame.grain }
  if (frame.preset === 'custom') {
    return { ...grain, from: frame.window.from, to: frame.window.to }
  }
  return { ...grain, range: frame.preset }
}

/** Apply a time frame to a `URLSearchParams`, clearing the keys it does not use. */
export function applyTimeFrame(params: URLSearchParams, frame: ReportTimeFrame): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of ['range', 'from', 'to', 'grain']) next.delete(key)
  for (const [key, value] of Object.entries(timeFrameParams(frame))) next.set(key, value)
  return next
}

function isIsoDay(value: string | null): value is string {
  return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(parseIsoDay(value).getTime())
}
